import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    if (this.dataset.stockFirst === 'true') this.#loadStockPage();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    this.#stockController?.abort();
    window.removeEventListener('popstate', this.#stockHistory);
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #stockController;

  #stockHistory = () => window.location.reload();

  async #loadStockPage() {
    window.addEventListener('popstate', this.#stockHistory);
    const url = new URL(window.location.href);
    if (this.dataset.stockState === 'error' || (this.dataset.stockState === 'ready' && url.searchParams.get('sort_by'))) return;
    if (!url.searchParams.get('sort_by')) {
      url.searchParams.set('sort_by', 'created-descending');
      history.replaceState(history.state, '', url);
    }
    const retry = this.querySelector('[data-stock-retry]');
    if (retry) retry.setAttribute('href', url.toString());
    this.#stockController = new AbortController();
    const signal = AbortSignal.any([this.#stockController.signal, AbortSignal.timeout(20000)]);
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, new URL(url), signal);
      if (signal.aborted || !this.isConnected || window.location.href !== url.toString()) return;
      await morphSection(this.sectionId, html, { injectStylesheet: true });
    } catch (error) {
      if (this.isConnected && !this.#stockController.signal.aborted) this.dataset.stockState = 'error';
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}


